import { Compass, Sun, Moon, Monitor, ArrowsClockwise, Lightbulb, Palette, GridFour } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../lib/theme";
import { fetchIdeasUntriagedCount } from "../lib/ideasApi";

export default function TopBar({ onRefresh, refreshing, onHome }) {
  const { theme, effective, cycle } = useTheme();
  const navigate = useNavigate();
  const [ideasCount, setIdeasCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const data = await fetchIdeasUntriagedCount();
      if (!cancelled) setIdeasCount(data?.count ?? 0);
    };
    tick();
    // Poll every 60s so the badge stays roughly in sync without hammering the
    // public endpoint. Refresh button on each page also triggers a fresh fetch
    // via its own reload paths.
    const id = setInterval(tick, 60000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const ThemeIcon = theme === "system" ? Monitor : theme === "light" ? Sun : Moon;
  const themeLabel = theme === "system" ? `System (${effective})` : theme[0].toUpperCase() + theme.slice(1);

  return (
    <header
      className="sticky top-0 z-30 border-b"
      style={{ borderColor: "hsl(var(--border))", background: "hsl(var(--background) / 0.95)", backdropFilter: "saturate(180%) blur(8px)" }}
      data-testid="mp-top-bar"
    >
      <div className="mx-auto flex h-14 max-w-[1400px] items-center gap-2 sm:gap-4 px-3 sm:px-6">
        {/* cycle-5c+25-fix16: Owner pick — brand block is now a 'home' button
            that resets page state (URL params, filters, narrow view, scroll).
            cycle-5c+25-fix22: padding tightened at <sm so brand + actions fit
            on 360px Android viewports without collision. */}
        <button
          type="button"
          onClick={onHome}
          className="flex items-center gap-2 sm:gap-3 rounded-md px-1 -mx-1 transition-colors hover:bg-[hsl(var(--muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="mp-brand-block-home-button"
          aria-label="Home — reset filters and selected card"
          title="Home — reset filters and selected card"
        >
          <img
            src={effective === "dark" ? "/logo_mark_dark.svg" : "/logo_mark_white.svg"}
            alt="LiftTech"
            className="h-7 w-7 shrink-0"
          />
          <div className="flex flex-col leading-tight text-left">
            <span className="text-sm font-semibold" style={{ color: "hsl(var(--foreground))" }}>Roadmap</span>
            <span
              className="text-mono hidden sm:block"
              style={{ fontSize: "0.65rem", letterSpacing: "0.05em", color: "hsl(var(--muted-foreground))" }}
            >
              Master Planner
            </span>
          </div>
        </button>

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => navigate("/board")}
            className="inline-flex items-center gap-1.5 rounded-md border px-2 sm:px-3 py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 hover:border-[hsl(var(--primary))]"
            style={{ borderColor: "hsl(var(--border))", color: "hsl(var(--foreground))" }}
            title="Vision Board — plain-English wall of slates, one per app"
            data-testid="mp-vision-board-btn"
            aria-label="Open Vision Board"
          >
            <GridFour size={12} />
            <span className="hidden sm:inline">Board</span>
          </button>
          <button
            type="button"
            onClick={() => navigate("/ideas")}
            className="inline-flex items-center gap-1.5 rounded-md border px-2 sm:px-3 py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 hover:border-[hsl(var(--primary))]"
            style={{ borderColor: "hsl(var(--border))", color: "hsl(var(--foreground))" }}
            title="Cross-product idea ingress (ADR-MP-013)"
            data-testid="mp-ideas-inbox-btn"
            aria-label={`Ideas Inbox${ideasCount ? ` — ${ideasCount} untriaged` : ""}`}
          >
            <Lightbulb size={12} />
            <span className="hidden sm:inline">Ideas</span>
            {ideasCount > 0 && (
              <span
                className="ml-1 inline-flex items-center justify-center rounded-full text-mono"
                style={{
                  background: "hsl(var(--status-warning))",
                  color: "hsl(var(--background))",
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  minWidth: "1.1rem",
                  height: "1.1rem",
                  padding: "0 0.3rem",
                }}
                data-testid="mp-ideas-inbox-badge"
              >
                {ideasCount > 99 ? "99+" : ideasCount}
              </span>
            )}
          </button>
          <a
            href="/brand/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border px-2 sm:px-3 py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 hover:border-[hsl(var(--primary))]"
            style={{ borderColor: "hsl(var(--border))", color: "hsl(var(--foreground))" }}
            title="Brand asset catalogue — URL-addressable logos, icons, favicons"
            data-testid="mp-brand-catalogue-btn"
            aria-label="Brand asset catalogue (opens in new tab)"
          >
            <Palette size={12} />
            <span className="hidden sm:inline">Brand</span>
          </a>
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-md border px-2 sm:px-3 py-1.5 text-xs transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2"
            style={{ borderColor: "hsl(var(--border))", color: "hsl(var(--foreground))" }}
            title="Re-read canon markdown"
            data-testid="mp-refresh-btn"
            aria-label={refreshing ? "Reading canon" : "Refresh canon"}
          >
            <ArrowsClockwise size={12} className={refreshing ? "animate-spin" : ""} />
            <span className="hidden sm:inline">{refreshing ? "Reading…" : "Refresh"}</span>
          </button>
          <button
            type="button"
            onClick={cycle}
            className="inline-flex items-center gap-1.5 rounded-md border px-2 sm:px-3 py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2"
            style={{ borderColor: "hsl(var(--border))", color: "hsl(var(--foreground))" }}
            title={`Theme: ${themeLabel}. Click to cycle.`}
            data-testid="mp-theme-toggle"
            aria-label={`Theme: ${themeLabel}`}
          >
            <ThemeIcon size={12} />
            <span className="hidden sm:inline">{themeLabel}</span>
          </button>
        </div>
      </div>
    </header>
  );
}
